import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Container, Grid, Typography, Button, Stack, IconButton, alpha, useTheme } from '@mui/material';
import { School, ChevronLeft, ChevronRight, Verified, EmojiEvents, Groups, ArrowForward, AccountBalance, Flight, CheckCircle } from '@mui/icons-material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import EnquiryForm from '../common/EnquiryForm';
import { scrollToSection } from '../../utils/scroll';

// ---------- Country Slide Data ----------
const slides = [
  {
    id: 1,
    country: 'Bangladesh',
    flag: '🇧🇩',
    tag: '🎓 SAARC Neighbor | Affordable',
    title: 'MBBS in',
    highlight: 'Bangladesh',
    subtitle: '35+ NMC-Approved Medical Colleges · No Language Barrier',
    description:
      'Bangladesh has emerged as a preferred destination for Indian students seeking affordable and quality medical education. With NMC-approved universities and English-medium instruction, Bangladesh offers a seamless pathway to your medical career.',
    cta1: { text: 'Explore Colleges', icon: <School /> },
    cta2: { text: 'Get Free Counselling', icon: <ArrowForward /> },
    image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=1800&q=80',
    color: '#006a4e',
    accent: '#f42a41',
    stats: [
      { icon: <School />, value: '35+', label: 'Medical Colleges' },
      { icon: <CheckCircle />, value: 'NMC', label: 'Approved' },
      { icon: <AccountBalance />, value: '$3,000', label: 'Avg Tuition/yr' },
      { icon: <Flight />, value: '1 hr', label: 'Flight from India' },
    ],
    trustTags: ['NMC Approved Colleges', 'English Medium', 'High FMGE Pass Rate'],
  },
  {
    id: 2,
    country: 'Nepal',
    flag: '🇳🇵',
    tag: '🏔️ Himalayan Neighbor | Trusted',
    title: 'MBBS in',
    highlight: 'Nepal',
    subtitle: '20+ NMC-Approved Colleges · No Donation · Indian Curriculum',
    description:
      'Nepal offers one of the most accessible MBBS programs for Indian students, with no mandatory donation fees and a curriculum closely aligned with the Indian medical education system. Its proximity and cultural familiarity make it a top choice.',
    cta1: { text: 'Explore Colleges', icon: <School /> },
    cta2: { text: 'Get Free Counselling', icon: <ArrowForward /> },
    image: 'https://images.unsplash.com/photo-1590682680695-43b964a3ae17?w=1800&q=80',
    color: '#003893',
    accent: '#DC143C',
    stats: [
      { icon: <School />, value: '20+', label: 'Medical Colleges' },
      { icon: <CheckCircle />, value: 'NMC', label: 'Approved' },
      { icon: <AccountBalance />, value: '$4,000', label: 'Avg Tuition/yr' },
      { icon: <Flight />, value: '2 hrs', label: 'Flight from India' },
    ],
    trustTags: ['No Donation Fees', 'WHO Recognized', 'Indian Curriculum'],
  },
  {
    id: 3,
    country: 'Russia',
    flag: '🇷🇺',
    tag: '🌍 Global Leader | World-Class',
    title: 'MBBS in',
    highlight: 'Russia',
    subtitle: '50+ NMC-Approved Universities · Globally Recognized Degrees',
    description:
      'Russia is home to some of the world\'s oldest and most prestigious medical universities. With globally recognized degrees, advanced infrastructure, and a strong focus on research, Russian medical education opens doors worldwide.',
    cta1: { text: 'Explore Universities', icon: <School /> },
    cta2: { text: 'Get Free Counselling', icon: <ArrowForward /> },
    image: 'https://images.unsplash.com/photo-1562774053-701939374585?w=1800&q=80',
    color: '#0039A6',
    accent: '#D52B1E',
    stats: [
      { icon: <School />, value: '50+', label: 'Medical Universities' },
      { icon: <CheckCircle />, value: 'NMC', label: 'Approved' },
      { icon: <AccountBalance />, value: '$3,500', label: 'Avg Tuition/yr' },
      { icon: <Flight />, value: '6 hrs', label: 'Flight from India' },
    ],
    trustTags: ['WHO & ECFMG Approved', 'English Medium', 'Global Career Pathway'],
  },
];

// ---------- Custom Arrow Components ----------
const arrowBaseStyles = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  bgcolor: 'rgba(47,37,34,0.6)',
  backdropFilter: 'blur(10px)',
  color: 'white',
  width: { xs: 48, md: 56 },
  height: { xs: 48, md: 56 },
  opacity: { xs: 1, md: 0 },
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    bgcolor: 'rgba(138,33,29,0.9)',
    transform: 'translateY(-50%) scale(1.1)',
    boxShadow: '0 8px 25px rgba(138,33,29,0.4)',
  },
  '.slick-slider:hover &': {
    opacity: 1,
  },
  boxShadow: '0 4px 20px rgba(47,37,34,0.3)',
  border: '1px solid rgba(255,255,255,0.2)',
};

const PrevArrow = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    aria-label="Previous slide"
    sx={{
      ...arrowBaseStyles,
      left: { xs: 12, md: 32 },
      right: 'auto',
    }}
  >
    <ChevronLeft sx={{ fontSize: 28 }} />
  </IconButton>
);

const NextArrow = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    aria-label="Next slide"
    sx={{
      ...arrowBaseStyles,
      right: { xs: 12, md: 32 },
      left: 'auto',
    }}
  >
    <ChevronRight sx={{ fontSize: 28 }} />
  </IconButton>
);

// ---------- Dots Component ----------
const Dots = ({ activeIndex, total, onClick, slides }) => (
  <Stack
    direction="row"
    spacing={2}
    justifyContent="center"
    sx={{
      position: 'absolute',
      bottom: { xs: 80, md: 40 },
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 10,
    }}
  >
    {slides.map((slide, index) => (
      <Box
        key={index}
        onClick={() => onClick(index)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: activeIndex === index ? 2 : 1.5,
          py: 0.6,
          borderRadius: 20,
          bgcolor: activeIndex === index ? alpha(slide.color, 0.85) : 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(8px)',
          cursor: 'pointer',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          border: activeIndex === index ? `2px solid ${slide.color}` : '2px solid rgba(255,255,255,0.3)',
          boxShadow: activeIndex === index ? `0 0 20px ${alpha(slide.color, 0.5)}` : '0 2px 5px rgba(47,37,34,0.3)',
          '&:hover': {
            bgcolor: activeIndex === index ? alpha(slide.color, 0.9) : 'rgba(255,255,255,0.35)',
            transform: 'scale(1.05)',
          },
        }}
      >
        <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>{slide.flag}</Typography>
        <Typography
          sx={{
            color: '#ffffff',
            fontWeight: activeIndex === index ? 800 : 600,
            fontSize: '0.75rem',
            textShadow: '0px 1px 4px rgba(47,37,34,0.6)',
            display: { xs: activeIndex === index ? 'block' : 'none', sm: 'block' },
          }}
        >
          {slide.country}
        </Typography>
      </Box>
    ))}
  </Stack>
);

// ---------- Main HeroSection Component ----------
const HeroSection = () => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);
  const sliderRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(88);

  // Measure the actual header height on mount and on resize
  useEffect(() => {
    const measureHeader = () => {
      const header = document.getElementById('main-header');
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    measureHeader();

    const observer = new ResizeObserver(measureHeader);
    const header = document.getElementById('main-header');
    if (header) {
      observer.observe(header);
    }

    return () => observer.disconnect();
  }, []);

  // ---------- Stats Data (inside component to access theme) ----------
  const stats = [
    { icon: <Groups sx={{ fontSize: { xs: 24, md: 32 } }} />, value: '5000+', label: 'Students Placed', color: theme.palette.primary.main },
    { icon: <School sx={{ fontSize: { xs: 24, md: 32 } }} />, value: '100+', label: 'Partner Colleges', color: theme.palette.success.main },
    { icon: <EmojiEvents sx={{ fontSize: { xs: 24, md: 32 } }} />, value: '15+', label: 'Years Experience', color: theme.palette.accent.main },
    { icon: <Verified sx={{ fontSize: { xs: 24, md: 32 } }} />, value: '98%', label: 'Success Rate', color: theme.palette.info.main },
  ];

  useEffect(() => {
    setMounted(true);
  }, []);

  const settings = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 900,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    fade: true,
    cssEase: 'cubic-bezier(0.7, 0, 0.3, 1)',
    pauseOnHover: false,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    beforeChange: (current, next) => {
      setActiveIndex(next);
    },
  };

  const goToSlide = useCallback((index) => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        '& .slick-slider': {
          '&:hover .slick-arrow': {
            opacity: 1,
          },
        },
      }}
    >
      {/* Slider */}
      <Box sx={{ position: 'relative' }}>
        <Slider ref={sliderRef} {...settings}>
          {slides.map((slide) => (
            <Box key={slide.id}>
              {/* Slide Background with Full Image and Gradient Overlay */}
              <Box
                sx={{
                  position: 'relative',
                  minHeight: { xs: `calc(100dvh - ${headerHeight}px)`, md: '92vh' },
                  height: { xs: 'auto', md: '92vh' },
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  background: `url(${slide.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(slide.color, 0.75)} 0%, ${alpha(theme.palette.secondary.main, 0.6)} 50%, ${alpha(slide.accent, 0.35)} 100%)`,
                    zIndex: 1,
                  },
                }}
              >
                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 3, py: { xs: 3, md: 0 }, minHeight: { xs: '100%', md: 'auto' }, display: 'flex', alignItems: 'center' }}>
                  <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
                    {/* Left Content */}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          maxWidth: 700,
                          animation: mounted ? 'fadeSlideUp 0.6s ease forwards' : 'none',
                          opacity: 0,
                          '@keyframes fadeSlideUp': {
                            '0%': { opacity: 0, transform: 'translateY(20px)' },
                            '100%': { opacity: 1, transform: 'translateY(0)' },
                          },
                        }}
                      >
                        {/* Title */}
                        <Typography
                          variant="h1"
                          sx={{
                          fontSize: { xs: '1.15rem', sm: '1.8rem', md: '3.8rem', lg: '4.4rem' },
                            fontWeight: 900,
                            mb: 1.5,
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            color: '#ffffff',
                            textShadow: `0px 3px 10px ${alpha(theme.palette.secondary.main, 0.6)}, 0px 6px 25px ${alpha(theme.palette.secondary.main, 0.4)}`,
                          }}
                        >
                          {slide.title}{' '}
                          <Box
                            component="span"
                            sx={{
                              color: '#ffffff',
                              fontWeight: 900,
                              textShadow: `0px 3px 10px ${alpha(theme.palette.secondary.main, 0.8)}, 0px 0px 30px ${alpha(slide.color, 0.3)}, 0px 6px 25px ${alpha(theme.palette.secondary.main, 0.4)}`,
                            }}
                          >
                            {slide.highlight}
                          </Box>
                        </Typography>

                        {/* Subtitle */}
                        <Typography
                          variant="h5"
                          sx={{
                            mb: { xs: 1.5, md: 2 },
                            fontWeight: 700,
                            fontSize: { xs: '0.7rem', sm: '0.95rem', md: '1.4rem' },
                            color: '#ffffff',
                            letterSpacing: '0.01em',
                            textShadow: `0px 2px 8px ${alpha(theme.palette.secondary.main, 0.7)}, 0px 0px 15px ${alpha(theme.palette.secondary.main, 0.3)}`,
                          }}
                        >
                          {slide.subtitle}
                        </Typography>

                        {/* Description */}
                        <Typography
                          variant="body1"
                          sx={{
                            mb: { xs: 1.5, md: 4.5 },
                            maxWidth: 600,
                            fontSize: { xs: '0.7rem', sm: '0.9rem', md: '1.15rem' },
                            fontWeight: 500,
                            lineHeight: 1.7,
                            color: '#ffffff',
                            textShadow: `0px 2px 6px ${alpha(theme.palette.secondary.main, 0.7)}, 0px 0px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
                          }}
                        >
                          {slide.description}
                        </Typography>

                        {/* Mini Stats Row - Grid layout */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, auto)' },
                            gap: { xs: 0.5, sm: 1.5 },
                            mb: { xs: 1.5, md: 3.5 },
                          }}
                        >
                          {slide.stats.map((stat, i) => (
                            <Box
                              key={i}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.8,
                                bgcolor: 'rgba(255,255,255,0.12)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: 2,
                                px: 1.5,
                                py: 0.8,
                                border: '1px solid rgba(255,255,255,0.2)',
                              }}
                            >
                              <Box sx={{ color: '#ffffff', fontSize: '0.9rem', display: 'flex' }}>
                                {stat.icon}
                              </Box>
                              <Box>
                                <Typography
                                  sx={{
                                    color: '#ffffff',
                                    fontWeight: 800,
                                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                    lineHeight: 1.2,
                                    textShadow: `0px 2px 4px ${alpha(theme.palette.secondary.main, 0.6)}`,
                                  }}
                                >
                                  {stat.value}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                  fontWeight: 600,
                                  fontSize: '0.6rem',
                                  lineHeight: 1,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.03em',
                                }}
                              >
                                {stat.label}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>

                      {/* CTA Buttons */}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        mb={{ xs: 2, md: 4.5 }}
                      >
                        <Button
                          variant="contained"
                          color="secondary"
                          size="large"
                          endIcon={slide.cta1.icon}
                          onClick={() => scrollToSection('contact')}
                          sx={{
                            px: 4.5,
                            py: 1.5,
                            fontSize: { xs: '0.8rem', sm: '1rem' },
                            fontWeight: 800,
                            borderRadius: 50,
                            bgcolor: slide.color,
                            border: '2px solid rgba(255,255,255,0.3)',
                            boxShadow: `0 8px 25px ${alpha(slide.color, 0.5)}, 0 4px 10px ${alpha(theme.palette.secondary.main, 0.3)}`,
                            '&:hover': {
                              bgcolor: slide.accent,
                              border: '2px solid rgba(255,255,255,0.5)',
                              boxShadow: `0 12px 35px ${alpha(slide.color, 0.6)}`,
                              transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          {slide.cta1.text}
                        </Button>
                        <Button
                          variant="outlined"
                          size="large"
                          endIcon={slide.cta2.icon}
                          onClick={() => scrollToSection('contact')}
                          sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: { xs: '0.8rem', sm: '1rem' },
                            fontWeight: 700,
                            color: '#ffffff',
                            borderColor: 'rgba(255,255,255,0.9)',
                            borderRadius: 50,
                            backdropFilter: 'blur(4px)',
                            bgcolor: alpha(theme.palette.secondary.main, 0.25),
                            textShadow: `0px 1px 3px ${alpha(theme.palette.secondary.main, 0.6)}`,
                            boxShadow: `0 4px 10px ${alpha(theme.palette.secondary.main, 0.2)}`,
                            '&:hover': {
                              borderColor: 'rgba(255,255,255,1)',
                              color: '#ffffff',
                              bgcolor: alpha(theme.palette.secondary.main, 0.5),
                              transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          {slide.cta2.text}
                        </Button>
                      </Stack>

                      {/* Trust Indicators */}
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        {slide.trustTags.map((tag, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                            }}
                          >
                            <Verified sx={{ color: '#ffffff', fontSize: 20, filter: `drop-shadow(0px 2px 4px ${alpha(theme.palette.secondary.main, 0.6)})` }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#ffffff',
                                fontWeight: 700,
                                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                                textShadow: `0px 2px 4px ${alpha(theme.palette.secondary.main, 0.7)}`,
                              }}
                            >
                              {tag}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </Box>
        ))}
      </Slider>

      {/* Persistent Enquiry Form - Mounted outside slider to preserve form state on slide change */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          top: '50%',
          right: { xs: 12, md: 32 },
          transform: 'translateY(-50%)',
          zIndex: 20,
        }}
      >
        <Box
          sx={{
            bgcolor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(24px)',
            borderRadius: 4,
            p: 4,
            width: 380,
            maxWidth: '90vw',
            boxShadow: `0 25px 60px ${alpha(theme.palette.secondary.main, 0.3)}`,
            animation: mounted ? 'fadeSlideUp 0.6s ease 0.2s forwards' : 'none',
            opacity: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              mb: 0.5,
              color: theme.palette.secondary.main,
              textAlign: 'center',
            }}
          >
            Get Free Counselling
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 3,
              textAlign: 'center',
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          >
            Fill in your details and we'll call you back
          </Typography>
          <EnquiryForm variant="popup" />
        </Box>
      </Box>
    </Box>

    {/* Stats Bar */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          mt: '-1px',
          bgcolor: theme.palette.secondary.main,
          borderTop: 1,
          borderColor: alpha('#ffffff', 0.06),
          py: { xs: 1.5, md: 3 },
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 1, md: 3 }} alignItems="center" justifyContent="center">
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Stack
                direction="row"
                spacing={{ xs: 1.5, md: 2 }}
                alignItems="center"
                justifyContent="center"
                sx={{
                  py: 1,
                  position: 'relative',
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    color: stat.color,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {stat.icon}
                </Box>

                {/* Text Stack */}
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '1.1rem', sm: '1.6rem', md: '1.9rem' },
                      color: stat.color,
                      lineHeight: 1.2,
                      letterSpacing: '-0.01em',
                      mb: 0.2,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontWeight: 600,
                      fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                      lineHeight: 1.3,
                      whiteSpace: { xs: 'normal', md: 'nowrap' },
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>

                {/* Vertical Divider (except last item) */}
                {index < stats.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '1px',
                      height: { xs: '50%', md: '60%' },
                      bgcolor: alpha('#ffffff', 0.1),
                      display: { xs: index % 2 === 0 ? 'block' : 'none', md: 'block' },
                    }}
                  />
                )}
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  </Box>
);
};

export default HeroSection;